import React, { ChangeEvent } from 'react';
import Downshift from 'downshift';
import { Input } from './Input/Input';
import { List } from '@chakra-ui/react';
import { ListItem } from './ListItem';
import { Box } from './Box';
import { Text } from './Text';
import { isEmpty } from 'lodash';
import { InputGroup } from './Input/InputGroup';
import { InputRightElement } from './Input/InputRightElement';
import { CloseIcon } from '@chakra-ui/icons';
import { Spinner } from './Spinner';
import { Textarea } from './Input/Textarea';
import { FormHelperText } from './FormHelperText';

const listStyle = {
  borderRadius: '10px',
  overflow: 'auto',
  background: '#fff',
  zIndex: 1000,
  width: '100%',
  marginTop: '3px',
  border: '1px solid',
  borderColor: '#DEE2E6',
};

type LIST_POSITION = 'TOP' | 'BOTTOM';

const getListStyle = (listPosition: LIST_POSITION) => {
  if (listPosition === 'TOP') {
    return { ...listStyle, top: 'auto', bottom: '100%', marginBottom: '5px' };
  }
  return listStyle;
};

const listItemStyle = {
  paddingTop: '0.4rem',
  paddingBottom: '0.4rem',
  paddingLeft: '0.8rem',
  cursor: 'pointer',
};

interface AutoCompleteProps {
  onInputChange(event: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>): void;
  inputValue: string;
  options: Array<any>;
  onSelect(value: any): void;
  isMenuOpen: boolean;
  noOptionsText?: string;
  placeholder?: string;
  getOptionLabel(option: any): string;
  menuMaxHeigh?: string;
  onClearInput(): void;
  isLoading: boolean;
  listPosition?: 'TOP' | 'BOTTOM';
  onFocus?(): void;
  onBlur?(event: React.FocusEvent<HTMLElement>): void;
  onCloseMenu(): void;
  testId?: string;
  isMultiLine?: boolean;
  maxLength?: number;
  autoFocus?: boolean;
}

function AutoComplete(props: AutoCompleteProps) {
  const {
    onInputChange,
    inputValue,
    options,
    onSelect,
    isMenuOpen,
    noOptionsText,
    placeholder,
    getOptionLabel,
    menuMaxHeigh,
    onClearInput,
    isLoading,
    listPosition,
    onFocus,
    onBlur,
    onCloseMenu,
    testId,
    isMultiLine,
    maxLength,
    autoFocus,
  } = props;

  return (
    <Box position={'relative'} w={'100%'}>
      <Downshift onChange={onSelect} itemToString={(item) => (item ? item.value : '')}>
        {({ getInputProps, getItemProps, getMenuProps, highlightedIndex }) => (
          <div>
            <InputGroup>
              {isMultiLine ? (
                <Textarea
                  pr={'28px'}
                  w={'100%'}
                  {...getInputProps()}
                  value={inputValue}
                  onBlur={(e) => {
                    onCloseMenu();

                    if (!onBlur) return;
                    onBlur(e);
                  }}
                  // override comportamento do downshift
                  onKeyDown={() => { }}
                  onChange={onInputChange}
                  placeholder={placeholder}
                  onFocus={(e) => {
                    const length = e.target.value.length;
                    e.target.setSelectionRange(length, length);
                    if (!onFocus) return;
                    onFocus();
                  }}
                  data-testid={testId}
                  shouldResize
                  maxLength={maxLength}
                  autoFocus={autoFocus}
                />
              ) : (
                <Input
                  pr={'28px'}
                  w={'100%'}
                  {...getInputProps()}
                  value={inputValue}
                  onBlur={(e) => {
                    onCloseMenu();

                    if (!onBlur) return;
                    onBlur(e);
                  }}
                  onChange={onInputChange}
                  placeholder={placeholder}
                  onFocus={(e) => {
                    const length = e.target.value.length;
                    e.target.setSelectionRange(length, length);
                    if (!onFocus) return;
                    onFocus();
                  }}
                  data-testid={testId}
                  maxLength={maxLength}
                  autoFocus={autoFocus}
                />
              )}

              <InputRightElement>
                {isLoading && <Spinner size={'sm'} color={'primary.main'} />}
                {inputValue.length > 0 && (
                  <CloseIcon
                    bg={'white'}
                    fontSize={'10px'}
                    cursor={'pointer'}
                    onClick={onClearInput}
                    color={'typography.dark'}
                    data-testid={'clear-icd-input'}
                  />
                )}
              </InputRightElement>
            </InputGroup>

            {maxLength && (
              <FormHelperText
                color={inputValue.length > maxLength ? '#D33618' : 'inherit'}
                textAlign={'right'}
              >{`${inputValue.length}/${maxLength}`}</FormHelperText>
            )}

            {isMenuOpen && !isLoading && !isEmpty(options) && (
              <List
                {...getMenuProps({
                  style: {
                    ...getListStyle(listPosition || 'BOTTOM'),
                    position: 'absolute',
                    maxHeight: menuMaxHeigh || '200px',
                  },
                })}
              >
                {options.map((item, index) => {
                  const itemProps = getItemProps({
                    index,
                    item,
                    style: {
                      ...listItemStyle,
                      backgroundColor: highlightedIndex === index ? '#F1F3F5' : 'white',
                    },
                  });
                  return (
                    <ListItem key={item.id || index} {...itemProps}>
                      <Text fontSize={'14px'}>{getOptionLabel(item)}</Text>
                    </ListItem>
                  );
                })}
              </List>
            )}

            {isMenuOpen && !isLoading && isEmpty(options) && noOptionsText && (
              <Box pos={'absolute'} {...listItemStyle} {...getListStyle(listPosition || 'BOTTOM')}>
                <Text fontSize={'14px'}>{noOptionsText}</Text>
              </Box>
            )}
          </div>
        )}
      </Downshift>
    </Box>
  );
}

export default AutoComplete;
